import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Link as LinkIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  Organization,
  getOrganizationUsers,
  getOrganizationBrands,
  associateBrandToOrganization,
} from "../../api/organization";
import { listCopywriters, Copywriter } from "../../api/client";
import { ClientDialog } from "./client-dialog";
import { CopywriterClientDialog } from "./copywriter-client-dialog";
import { BrandAssociationDialog } from "./brand-association-dialog";
import { OrganizationUsage } from "./organization-usage";
import { Brand, Role } from "../../pages/playground/types";

interface User {
  id: string;
  name: string;
  surname: string;
  email: string;
  role: Role;
  email_confirmed: boolean;
  disabled: boolean;
}

interface OrganizationDetailViewProps {
  organization: Organization;
  onBack: () => void;
  onEdit: () => void;
  onDelete: (organizationId: string) => void;
  onRefresh: () => void;
}

export const OrganizationDetailView: React.FC<OrganizationDetailViewProps> = ({
  organization,
  onBack,
  onEdit,
  onRefresh,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [copywriterClients, setCopywriterClients] = useState<Copywriter[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);
  const [isLoadingCopywriterClients, setIsLoadingCopywriterClients] =
    useState(false);

  // Dialog states
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [showCopywriterClientDialog, setShowCopywriterClientDialog] =
    useState(false);
  const [showBrandAssociationDialog, setShowBrandAssociationDialog] =
    useState(false);
  const [confirmDissociate, setConfirmDissociate] = useState<{
    brandId: string;
    brandName: string;
  } | null>(null);
  const [editingClient, setEditingClient] = useState<User | null>(null);
  const [editingCopywriterClient, setEditingCopywriterClient] =
    useState<Copywriter | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const response = await getOrganizationUsers(organization.id);
      const userData = response?.data?.data || [];
      setUsers(userData);
    } catch (error) {
      console.error("Error fetching organization users:", error);
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [organization.id]);

  const fetchBrands = useCallback(async () => {
    setIsLoadingBrands(true);
    try {
      const response = await getOrganizationBrands(organization.id);
      const brandData = response?.data?.data || [];
      setBrands(brandData);
    } catch (error) {
      console.error("Error fetching organization brands:", error);
      setBrands([]);
    } finally {
      setIsLoadingBrands(false);
    }
  }, [organization.id]);

  const fetchCopywriterClients = useCallback(async () => {
    setIsLoadingCopywriterClients(true);
    try {
      const response = await listCopywriters(organization.id);
      const copywriterData = response?.data?.data || [];
      setCopywriterClients(copywriterData);
    } catch (error) {
      setCopywriterClients([]);
    } finally {
      setIsLoadingCopywriterClients(false);
    }
  }, [organization.id]);

  useEffect(() => {
    fetchUsers();
    fetchBrands();
    fetchCopywriterClients();
  }, [organization.id, fetchUsers, fetchBrands, fetchCopywriterClients]);

  const handleInviteClient = () => {
    setEditingClient(null);
    setShowClientDialog(true);
  };

  const handleEditClient = (user: User) => {
    setEditingClient(user);
    setShowClientDialog(true);
  };

  const handleInviteCopywriterClient = () => {
    setEditingCopywriterClient(null);
    setShowCopywriterClientDialog(true);
  };

  const handleEditCopywriterClient = (copywriter: Copywriter) => {
    setEditingCopywriterClient(copywriter);
    setShowCopywriterClientDialog(true);
  };

  const handleAssociateBrand = () => {
    setShowBrandAssociationDialog(true);
  };

  const handleBrandAssociation = async (brandId: string) => {
    try {
      await associateBrandToOrganization(organization.id, brandId);
      await fetchBrands();
    } catch (error) {
      console.error("Error associating brand:", error);
      throw error;
    }
  };

  const handleDissociateBrand = async () => {
    if (!confirmDissociate) return;

    try {
      await associateBrandToOrganization(null, confirmDissociate.brandId);
      await fetchBrands();
      onRefresh();
    } catch (error) {
      console.error("Error dissociating brand:", error);
      alert("Failed to dissociate brand. Please try again.");
    } finally {
      setConfirmDissociate(null);
    }
  };

  return (
    <>
      <Box>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5">{organization.name}</Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                mt: 1,
                alignItems: "center",
              }}
            >
              <>
                <Typography variant="body2" color="text.secondary">
                  Active Modes:
                </Typography>

                <Chip label="Review" size="small" variant="outlined" />

                {organization.mode_allow_create &&
                organization.mode_allow_ai_generation ? (
                  <Chip label="Create + AI" size="small" variant="outlined" />
                ) : (
                  <>
                    {organization.mode_allow_create && (
                      <Chip label="Create" size="small" variant="outlined" />
                    )}
                    {organization.mode_allow_ai_generation && (
                      <Chip label="AI Gen" size="small" variant="outlined" />
                    )}
                  </>
                )}

                {organization.mode_allow_translate &&
                organization.mode_allow_translate_ai ? (
                  <Chip
                    label="Translate + AI"
                    size="small"
                    variant="outlined"
                  />
                ) : (
                  <>
                    {organization.mode_allow_translate && (
                      <Chip label="Translate" size="small" variant="outlined" />
                    )}
                    {organization.mode_allow_translate_ai && (
                      <Chip
                        label="AI Translate"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </>
                )}
              </>
            </Box>
          </Box>
          <Button
            startIcon={<EditIcon />}
            variant="outlined"
            onClick={onEdit}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
        </Box>

        {/* Disabled Organization Alert */}
        {organization.disabled && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              This organization is disabled.
            </Typography>
            <Typography variant="body2">
              All users in this organization are prevented from logging in.
              Enable the organization to restore access.
            </Typography>
          </Alert>
        )}

        {organization.mode_allow_translate_ai && (
          <OrganizationUsage organizationId={organization.id} isAdmin />
        )}

        {/* Brands Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Brands</Typography>
            <Button
              startIcon={<LinkIcon />}
              variant="contained"
              size="small"
              onClick={handleAssociateBrand}
            >
              Associate Brand
            </Button>
          </Box>

          {isLoadingBrands ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : brands.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No brands in this organization
            </Typography>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 2,
              }}
            >
              {brands.map((brand) => (
                <Box
                  key={brand.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <Typography variant="body2">{brand.name}</Typography>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setConfirmDissociate({
                        brandId: brand.id || "",
                        brandName: brand.name || "",
                      })
                    }
                    sx={{ ml: 1 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        {/* Clients Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Client Admins</Typography>
            <Button
              startIcon={<PersonAddIcon />}
              variant="contained"
              size="small"
              onClick={handleInviteClient}
            >
              Invite Client Admin
            </Button>
          </Box>

          {isLoadingUsers ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : users.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No clients in this organization
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        {user.name} {user.surname}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            user.role !== "CLIENT" ? user.role : "Client Admin"
                          }
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {user.disabled ? (
                          <Chip label="Disabled" size="small" color="error" />
                        ) : (
                          <Chip
                            label={user.email_confirmed ? "Active" : "Pending"}
                            size="small"
                            color={user.email_confirmed ? "success" : "default"}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {
                          <IconButton
                            size="small"
                            onClick={() => handleEditClient(user)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Client Users</Typography>
            <Button
              startIcon={<PersonAddIcon />}
              variant="contained"
              size="small"
              onClick={handleInviteCopywriterClient}
            >
              Invite Client User
            </Button>
          </Box>

          {isLoadingCopywriterClients ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
            </Box>
          ) : copywriterClients.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No client users in this organization
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {copywriterClients.map((copywriter) => (
                    <TableRow key={copywriter.id}>
                      <TableCell>
                        {copywriter.name} {copywriter.surname}
                      </TableCell>
                      <TableCell>{copywriter.email}</TableCell>
                      <TableCell>
                        {copywriter.disabled ? (
                          <Chip label="Disabled" size="small" color="error" />
                        ) : (
                          <Chip
                            label={copywriter.status}
                            size="small"
                            color={
                              copywriter.status === "Joined"
                                ? "success"
                                : "warning"
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEditCopywriterClient(copywriter)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Client Invitation Dialog */}
      {showClientDialog && (
        <ClientDialog
          open={showClientDialog}
          client={editingClient}
          onClose={(refresh) => {
            setShowClientDialog(false);
            if (refresh) {
              fetchUsers();
              onRefresh();
            }
          }}
          onDisableEnable={() => {
            // Refresh the users list after disable/enable
            fetchUsers();
            onRefresh();
          }}
          defaultOrganizationId={organization.id}
        />
      )}

      {showCopywriterClientDialog && (
        <CopywriterClientDialog
          open={showCopywriterClientDialog}
          copywriter={editingCopywriterClient}
          onClose={(refresh) => {
            setShowCopywriterClientDialog(false);
            if (refresh) {
              fetchCopywriterClients();
              onRefresh();
            }
          }}
          onDisableEnable={() => {
            fetchCopywriterClients();
            onRefresh();
          }}
          organizationId={organization.id}
        />
      )}

      {/* Brand Association Dialog */}
      <BrandAssociationDialog
        open={showBrandAssociationDialog}
        organizationId={organization.id}
        organizationName={organization.name}
        onClose={(refresh) => {
          setShowBrandAssociationDialog(false);
          if (refresh) {
            fetchBrands();
            onRefresh();
          }
        }}
        onAssociate={handleBrandAssociation}
      />

      {/* Dissociate Confirmation Dialog */}
      <Dialog
        open={!!confirmDissociate}
        onClose={() => setConfirmDissociate(null)}
      >
        <DialogTitle>Dissociate Brand</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to dissociate "{confirmDissociate?.brandName}"
            from this organization?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDissociate(null)}>Cancel</Button>
          <Button
            onClick={handleDissociateBrand}
            color="error"
            variant="contained"
          >
            Dissociate
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
